import { toCanvas } from 'html-to-image'
import type { ReceiptKind } from '../components/receipt/Receipt'
import type { NetworkPrinter } from '../types'
import { buildJob, PRINT_DOTS, toMono, type MonoImage } from '../utils/escpos'
import { sendToPrinter } from './printBridge'

/**
 * Fişi ağ yazıcılarına dağıtır (20 Eki 2026).
 *
 * #receipt-root'taki her tezgah fişi (.r-slip) aynı tasarımla resme çevrilip
 * o tezgaha verilmiş yazıcıya gidiyor; kasa fişi "Kasa fişi" işaretli
 * yazıcılara. Tezgahı hiçbir yazıcıya verilmemişse "Diğer tezgahlar" işaretli
 * yazıcıya düşüyor, o da yoksa basılmıyor ve hata olarak söyleniyor.
 */

/** Bir fişin hangi yazıcılara gideceği. */
export function printersFor(kind: ReceiptKind, station: string, printers: NetworkPrinter[]): NetworkPrinter[] {
  if (kind === 'cash') return printers.filter((p) => p.cash)
  const own = printers.filter((p) => p.stations.includes(station))
  return own.length > 0 ? own : printers.filter((p) => p.others)
}

/** Kağıt genişliğine göre fişin çizildiği genişlik (receipt.css 72mm). */
const PAPER_WIDTH: Record<80 | 58, string> = { 80: '72mm', 58: '48mm' }

/**
 * İki yandaki boşluk, nokta (1,5 mm). Fiş kağıdın tam basılabilir genişliğine
 * çizilince yazılar en uçtaki noktalara dayanıyor; bazı yazıcılar onları
 * basmıyor (20 Eki 2026, deneme çıktısı).
 */
const SIDE = 12

async function rasterize(slip: HTMLElement, paper: 80 | 58): Promise<MonoImage> {
  const dots = PRINT_DOTS[paper]
  const drawn = await toCanvas(slip, {
    backgroundColor: '#fff',
    pixelRatio: (dots - 2 * SIDE) / slip.offsetWidth,
    // Web yazı tipleri dışarıdan değil, sayfadakinden: fiş zaten Courier.
    skipFonts: true,
  })
  const canvas = document.createElement('canvas')
  canvas.width = dots
  canvas.height = drawn.height
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, dots, drawn.height)
  ctx.drawImage(drawn, SIDE, 0)
  const { data, width, height } = ctx.getImageData(0, 0, dots, drawn.height)
  return toMono(data, width, height)
}

export interface NetworkPrintResult {
  /** Gönderilemeyenler, ekranda söylenecek: "Mutfak: Yazıcıya ulaşılamadı." */
  failed: string[]
}

/**
 * Ekrandaki fişi basar. Çağrıldığında fiş çizilmiş olmalı (App print, iki
 * rAF sonra).
 */
export async function printReceiptToNetwork(
  kind: ReceiptKind,
  printers: NetworkPrinter[],
): Promise<NetworkPrintResult> {
  const root = document.getElementById('receipt-root')
  const failed: string[] = []
  if (!root) return { failed }

  // Yazıcı başına fiş resimleri; kağıdı aynı olanlar için bir kez çiziliyor.
  const jobs = new Map<string, { printer: NetworkPrinter; images: MonoImage[] }>()
  const html = document.documentElement
  html.classList.add('r-raster')
  try {
    for (const paper of [80, 58] as const) {
      root.style.width = PAPER_WIDTH[paper]
      for (const slip of root.querySelectorAll<HTMLElement>('.r-slip')) {
        const station = slip.dataset.station ?? ''
        const all = printersFor(kind, station, printers)
        const targets = all.filter((p) => p.paper === paper)
        if (paper === 80 && all.length === 0) {
          const msg = kind === 'cash' ? 'Kasa fişi: yazıcı verilmemiş.' : `${station}: bu tezgaha yazıcı verilmemiş.`
          if (!failed.includes(msg)) failed.push(msg)
        }
        if (targets.length === 0) continue
        const img = await rasterize(slip, paper)
        for (const p of targets) {
          const job = jobs.get(p.id) ?? { printer: p, images: [] }
          job.images.push(img)
          jobs.set(p.id, job)
        }
      }
    }
  } finally {
    root.style.width = ''
    html.classList.remove('r-raster')
  }

  await Promise.all(
    [...jobs.values()].map(async ({ printer, images }) => {
      try {
        await sendToPrinter(printer.host, printer.port, buildJob(images))
      } catch (err) {
        failed.push(`${printer.name}: ${err instanceof Error ? err.message : 'gönderilemedi.'}`)
      }
    }),
  )
  return { failed }
}

/** Ayarlar'daki "Dene" düğmesi: küçük bir deneme fişi. */
export async function printTestPage(printer: NetworkPrinter): Promise<void> {
  const dots = PRINT_DOTS[printer.paper]
  const canvas = document.createElement('canvas')
  canvas.width = dots
  canvas.height = 220
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, dots, 220)
  ctx.fillStyle = '#000'
  ctx.textAlign = 'center'
  ctx.font = 'bold 40px "Courier New", monospace'
  ctx.fillText('ADISSOO', dots / 2, 60)
  ctx.font = 'bold 26px "Courier New", monospace'
  ctx.fillText(printer.name, dots / 2, 110)
  ctx.fillText(`${printer.host}:${printer.port}`, dots / 2, 150)
  ctx.fillText('Türkçe: ŞşĞğİıÖöÜüÇç', dots / 2, 190)
  const { data } = ctx.getImageData(0, 0, dots, 220)
  await sendToPrinter(printer.host, printer.port, buildJob([toMono(data, dots, 220)]))
}
